import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext.js";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { loginSchema } from "./schemas/authenticationSchema.js";

import Button from "../../components/Button.js";
import Input from "../../components/Input.js";

import { signIn } from "../../api/authRequest.js";

import LearnMoreSection from "../../components/LearnMore.js";
import { useTranslation } from "../../i18n/TranslationProvider";
import MfaForm from "../../components/MfaForm.js";

const SignIn = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const { t } = useTranslation();
    const [step, setStep] = useState("credentials"); // "credentials" | "mfa"
    const [userEmail, setUserEmail] = useState("");
    const [mfaCode, setMfaCode] = useState("");
    const [isSubmittingMfa, setIsSubmittingMfa] = useState(false);
    const [resendMessage, setResendMessage] = useState("");

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(loginSchema),
    });
    
    // Step 1: Handle Email + Password Submission
    const onCredentialsSubmit = async (data) => {
        setError("");
        try {
            const res = await signIn(data);

            if (res.data?.mfaRequired) {
                setUserEmail(data.email);
                setStep("mfa");
            }
        } catch (err) {
            console.error("Error logging in:", err);
            if (err.response?.status === 401) {
                setError(t("auth.loginErrorInvalid"));
            } else {
                setError(err.response?.data?.message || t("auth.loginErrorGeneric"));
            }
        }
    };

    // Step 2: Handle MFA Code Submission
    const onMfaSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setResendMessage("");
        setIsSubmittingMfa(true);

        try {
            const res = await verifyMfaCode(mfaCode);
            // On success, backend returns { userId } and regenerates session
            login(res.data.userId);
            navigate("/athletes");
        } catch (err) {
            console.error("Error verifying MFA code:", err);
            setError(err.response?.data?.message || t("auth.mfaInvalidCode"));
        } finally {
            setIsSubmittingMfa(false);
        }
    };

    // Handle Code Resend
    const handleResendCode = async () => {
        setError("");
        setResendMessage("");
        try {
            await resendMfaCode();
            setResendMessage(t("auth.codeResent") || "A new 6-digit code has been sent.");
        } catch (err) {
            setError(err.response?.data?.message || t("auth.resendFailed"));
        }
    };

    // const onSubmit = async (data) => {
    //     setError("");
    //     try {
    //         const res = await signIn(data);
    //         login(res.data.userId);
    //         navigate("/athletes");
    //     } catch (err) {
    //         console.error("Error logging in:", err);
    //         if (err.response?.status === 401) {
    //             setError(t("auth.loginErrorInvalid"));
    //         } else {
    //             setError(t("auth.loginErrorGeneric"));
    //         }
    //     }
    // };

   return (
        <div className="custom-container w-4/5 md:w-2/3 lg:w-1/2 mx-auto">
            <div className="text-base md:text-2xl text-center mx-auto mb-6">
                <h1 className="mb-2">{t("auth.login")}</h1>
                {step === "credentials" ? (
                    <p>{t("auth.loginSubheading")}</p>
                ): (
                    <p>{t("auth.mfaSentNotice")} <span className="text-primary">{userEmail}</span></p>
                )}
                
            </div>

            {step === "credentials" ? (
                /* Step 1: Credentials Form */
                <form className="flex flex-col gap-4" onSubmit={handleSubmit(onCredentialsSubmit)}>
                    <Input
                        label={t("auth.email")}
                        name="email"
                        register={register}
                        errors={errors}
                        placeholder={t("auth.emailPlaceholder")}
                        className="focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <Input
                        label={t("auth.password")}
                        name="password"
                        type="password"
                        register={register}
                        errors={errors}
                        placeholder={t("auth.passwordPlaceholder")}
                        className="focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <div>
                        <button
                            type="button"
                            className="text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
                            onClick={() => navigate("/forgot-password")}
                        >
                            {t("auth.forgotPasswordLink")}
                        </button>
                    </div>

                    {error && <p className="text-red-600">{error}</p>}

                    <Button
                        type="submit"
                        isLoading={isSubmitting}
                        className="text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                        {t("auth.login")}
                    </Button>
                </form>
            ) : (
                /* Step 2: MFA Code Verification Form */
               <MfaForm
                    onVerified={(userId) => {
                        login(userId);
                        navigate("/athletes");
                    }}
                    onBack={() => {
                        setStep("credentials");
                        setError("");
                    }}
                />
            )}

            <hr className="mt-6 border border-gray-200 w-full p-0 mx-auto" />
            
            <div className="mt-4 px-2 flex justify-between items-center">
                <p>{t("auth.noAccount")}</p>
                <button
                    type="button"
                    className="text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
                    onClick={() => navigate("/register")}
                >
                    {t("auth.register")}
                </button>
            </div>
            
            <div className="mt-2 px-2 flex justify-between items-center">
                <p>{t("auth.needHelp")}</p>
                <button
                    type="button"
                    className="text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
                    onClick={() => navigate("/help")}
                >
                    {t("auth.contactUs")}
                </button>
            </div>
        </div>
    );
};

export default SignIn;